<script setup lang="ts">
import { DBFactory } from "~/classes/database/db_factory";
import { SubmissionJob } from "~/classes/models/submission-job/submission-job.model";

enum SubmissionAction {
  fixSubmission = "fixSubmission",
  submitAsFinalDraft = "submitAsFinalDraft",
  resubmit = "resubmit",
}

const props = defineProps({
  submittedDocument: {
    type: Object as PropType<SubmittedDocument>,
    required: true,
  },
  classroomId: {
    type: String as PropType<string>,
    required: true,
  },
  studentId: {
    type: String as PropType<string>,
    required: true,
  },
});

const studentClassroomStore = useStudentClassroomDataStore(props.studentId);
const documentId = inject("documentId") as string;
const { access: writingToolsAccess } =
  useStudentWritingToolsAccess(documentId);

const { classroomFromId, assignments, student } = storeToRefs(
  studentClassroomStore
);

onMounted(() => {
  studentClassroomStore.initializeStudentClassroomData();
});

const canShowButton = computed(() => {
  const classroom = classroomFromId.value(props.classroomId);

  if (classroom == undefined) {
    return false;
  }

  if (writingToolsAccess.value.canWrite != true) {
    return false;
  }

  if (props.submittedDocument.state != DocumentState.graded) {
    return false;
  }

  if (
    props.submittedDocument.isReturnedToStudent != undefined &&
    props.submittedDocument.isReturnedToStudent != true
  ) {
    return false;
  }

  return (
    props.submittedDocument.documentSubmissionType !=
    DocumentSubmissionType.keyWordOutline
  );
});

const actions = computed(() => {
  if (
    props.submittedDocument.documentSubmissionType ==
    DocumentSubmissionType.roughDraft
  ) {
    return [SubmissionAction.submitAsFinalDraft];
  }

  if (
    props.submittedDocument.documentGradedState ==
    DocumentGradedState.incomplete
  ) {
    return [SubmissionAction.fixSubmission];
  }

  return [SubmissionAction.resubmit];
});

const label = (action: SubmissionAction) => {
  switch (action) {
    case SubmissionAction.fixSubmission:
      return "Revise Final Draft";
    case SubmissionAction.submitAsFinalDraft:
      return "Create Final Draft";
    case SubmissionAction.resubmit:
      return "Create New Version";
  }
};

const isCreatingSubmissionJob = ref(false);

const onClick = async (action: SubmissionAction) => {
  if (isCreatingSubmissionJob.value) return;

  const classroom = classroomFromId.value(props.classroomId);
  const assignment = assignments.value.find(
    (assignment) => assignment.id == props.submittedDocument.assignmentId
  );

  if (
    classroom == undefined ||
    assignment == undefined ||
    student.value == undefined
  ) {
    useBaseToast("Oops! Something went wrong. Please try again.", "error");
    return;
  }

  if (props.submittedDocument.id == undefined) {
    useBaseToast("Oops! Something went wrong. Please try again.", "error");
    return;
  }

  const revisionId = await useDocuments().getLatestDocumentRevisionId(
    props.submittedDocument.id
  );

  if (revisionId == undefined) {
    useBaseToast("Oops! Something went wrong. Please try again.", "error");
    return;
  }

  const db = DBFactory.createDatabase();
  let revision: Revision | undefined;
  let sourceStudentSubmissionOps: Revision["studentSubmissionOps"];

  try {
    const data = await db.get({
      path: `documents/${props.submittedDocument.id}/revisions/${revisionId}`,
      collection: `documents/${props.submittedDocument.id}/revisions`,
    });

    revision = {
      ...data,
      id: data.id,
    } as Revision;

    sourceStudentSubmissionOps = revision.studentSubmissionOps;
    if (!Array.isArray(sourceStudentSubmissionOps)) {
      // Grading creates a separate revision; the student's identifications live
      // on the most recent editor revision of the same paper.
      const revisions = await db.list({
        path: `documents/${props.submittedDocument.id}/revisions`,
        collection: `documents/${props.submittedDocument.id}/revisions`,
      }, [{ type: "orderBy", field: "lastUpdatedTimestamp", direction: "desc" }]);
      sourceStudentSubmissionOps = revisions.find(
        revision => Array.isArray(revision.studentSubmissionOps)
      )?.studentSubmissionOps;
    }
  } catch (e) {
    useBaseToast("Oops! Something went wrong. Please try again.", "error");
    return;
  }

  isCreatingSubmissionJob.value = true;

  const revisionParagraphsSnapshot = await db.list(
    {
      path: `documents/${props.submittedDocument.id}/revisions/${revisionId}/paragraphs`,
      collection: `documents/${props.submittedDocument.id}/revisions/${revisionId}/paragraphs`,
    },
    [
      {
        type: "orderBy",
        field: "index",
        direction: "asc",
      },
    ]
  );

  const revisionParagraphs = revisionParagraphsSnapshot.map((data) => {
    return {
      ...data,
      id: data.id,
    } as Paragraph;
  });

  const revisionAnnotationsSnapshot = await db.list({
    path: `documents/${props.submittedDocument.id}/revisions/${revisionId}/annotations`,
    collection: `documents/${props.submittedDocument.id}/revisions/${revisionId}/annotations`,
  });

  const revisionAnnotations = revisionAnnotationsSnapshot.map((data) => {
    return {
      ...data,
      id: data.id,
    } as RevisionAnnotation;
  });

  let submissionType = props.submittedDocument.documentSubmissionType;

  switch (action) {
    case SubmissionAction.submitAsFinalDraft:
      submissionType = DocumentSubmissionType.finalDraft;
      break;
    case SubmissionAction.fixSubmission:
    case SubmissionAction.resubmit:
      submissionType = props.submittedDocument.documentSubmissionType;
      break;
  }

  const quillDelta = useConvertParagraphsToDelta(
    revisionParagraphs,
    revisionAnnotations,
    revision.commentObject,
    props.submittedDocument.inkColor,
    true,
    sourceStudentSubmissionOps
  );

  const submissionJob = new SubmissionJob({
    userId: classroom.userId ?? student.value?.userId ?? useCurrentUID() ?? "",
    files: [],
    sheetController: useNewSheetController().value,
    uploadMethod: "create",
    documentSubmissionType: submissionType ?? DocumentSubmissionType.finalDraft,
    submittedDocumentState: DocumentState.editing,
    attachChecklist: props.submittedDocument.checklistId != undefined,
    accessToken: "",
    id: useGenerateUID(),
    classrooms: [],
    assignments: [],
    checklists: [],
    students: [],
    classroom: classroom,
    assignment: assignment,
    checklist: undefined,
    student: student.value,
    studentSubmissionOps: quillDelta.ops,
  });

  await submissionJob.createWritableFile(props.submittedDocument.name);

  isCreatingSubmissionJob.value = false;
};
</script>

<template>
  <div v-if="canShowButton" class="flex flex-col gap-2 mt-1 sm:flex-row">
    <button
      v-for="submissionAction in actions"
      :key="submissionAction"
      v-auto-animate
      type="button"
      :disabled="isCreatingSubmissionJob"
      class="cursor-pointer w-full text-center relative flex flex-row items-center justify-center p-2 border border-secondary-default bg-secondary-default rounded text-body-2 px-2 hover:bg-secondary/20 text-secondary-on disabled:cursor-wait disabled:opacity-70"
      @click.stop.prevent="onClick(submissionAction)"
    >
      {{ label(submissionAction) }}
      <div
        v-if="isCreatingSubmissionJob"
        class="w-full h-full absolute top-0 left-0 bg-dark/20 rounded flex items-center justify-center"
      >
        <BaseLoadingSpinner class="text-primary-on" size="14" />
      </div>
    </button>
  </div>
</template>

<style scoped></style>
